import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { showToast } from '@repo/ui';

import {
  clioCredentialsApi,
  type PublicCredentialBundle,
  type RegisterCredentialRequest,
  type RegisterCredentialResponse,
  type ReauthorizeRequest,
  type ReauthorizeResponse,
  type UpdateCredentialRequest,
} from '../api/clioCredentialsApi';

export const clioCredentialsQueryKeys = {
  all: ['clio', 'credentials'] as const,
  list: ['clio', 'credentials', 'list'] as const,
};

export function useClioCredentials() {
  return useQuery<PublicCredentialBundle>({
    queryKey: clioCredentialsQueryKeys.list,
    queryFn: () => clioCredentialsApi.list(),
    staleTime: 30 * 1000,
  });
}

export function useRegisterClioCredential() {
  const qc = useQueryClient();
  return useMutation<RegisterCredentialResponse, Error, RegisterCredentialRequest>({
    mutationFn: (input) => clioCredentialsApi.register(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: clioCredentialsQueryKeys.all });
    },
    onError: (err) => {
      showToast.error(`Failed to register Clio credential: ${err.message}`);
    },
  });
}

export function useUpdateClioCredential() {
  const qc = useQueryClient();
  return useMutation<
    unknown,
    Error,
    { credentialId: string; patch: UpdateCredentialRequest }
  >({
    mutationFn: ({ credentialId, patch }) =>
      clioCredentialsApi.update(credentialId, patch),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: clioCredentialsQueryKeys.all });
    },
    onError: (err) => {
      showToast.error(`Failed to update Clio credential: ${err.message}`);
    },
  });
}

export function useRemoveClioCredential() {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (credentialId) => clioCredentialsApi.remove(credentialId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: clioCredentialsQueryKeys.all });
      showToast.success('Clio credential removed');
    },
    onError: (err) => {
      showToast.error(`Failed to remove Clio credential: ${err.message}`);
    },
  });
}

export function useReauthorizeClioCredential() {
  return useMutation<
    ReauthorizeResponse,
    Error,
    { credentialId: string; request: ReauthorizeRequest }
  >({
    mutationFn: ({ credentialId, request }) =>
      clioCredentialsApi.reauthorize(credentialId, request),
    onError: (err) => {
      showToast.error(`Failed to start re-authorization: ${err.message}`);
    },
  });
}

/** Used by the OAuth popup success handler to refresh the list. */
export function useInvalidateClioCredentials() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: clioCredentialsQueryKeys.all });
}
